/**
 * @file contact.js
 * @fileoverview Renders the contact page.
 * @namespace contact
 * @description Renders the contact page.
 * @module common/assets/pages/Page/contact
 * @since 3.0.0
 * @todo Add the ability to render the contact page using the standard template logic.
 */

import {
    i18n,
    loading,
    outputDiv,
    userLanguage,
    siteInfo,
    appDomain,
} from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { showContactFAQForm } from "commonAssets/lib/showContactFAQForm.js";

export const contactPage = (params) => {
    console.log("[Contact Page] ...", params);
    // define the constants
    const slug = params.slug;
    const page = params.pages.find((page) => page.PageName === slug);
    // define the elements
    const pageTitle = createElement(
        "h2",
        { class: "page-title mt-2 mb-4" },
        [],
        page.PageTitle
    );
    const pageContent = createElement("div", { class: "page-content row" });
    const pageDiv = createElement("div", { class: "page-div" }, [
        pageTitle,
        pageContent,
    ]);
    const divImptDates = createElement(
        "div",
        {
            class: "my-5 mx-auto",
            id: "ImportantDates",
            style: "max-width: 512px;",
        },
        [],
        params.dates.card()
    );
    const pageColumns = createElement("div", { class: "col-12 col-md-8" });
    // contact column
    if (page.PageContent.length > 0) {
        // make standard contact page
        makeStandardContactPage(params, page, userLanguage, pageColumns);
        pageContent.appendChild(pageColumns);
    } else {
        const contactInfoNa = createElement(
            "p",
            { class: "fst-italic" },
            [],
            `No contact information found.`
        );
        pageColumns.appendChild(contactInfoNa);
        pageContent.appendChild(pageColumns);
    }
    // remove the my-5 and mx-auto classes from the divImptDates element
    if (divImptDates.classList.contains("my-5", "mx-auto")) {
        divImptDates.classList.remove("my-5", "mx-auto");
    }
    // important dates column
    const imptDatesColumn = createElement("div", { class: "col-12 col-md-4" }, [
        divImptDates,
    ]);
    pageContent.appendChild(imptDatesColumn);
    outputDiv.appendChild(pageDiv);
    loadingGif(loading, "hide", 250);
};

/**
 * makeStandardContactPage()
 *
 * Creates the contact page using the standard template logic
 *
 * @param {class} Page -   Instantiated Page class (should be passed using `this`)
 * @param {Object} page - dictionary object for the page being rendered from CMS
 * @returns {null} - Does not return a value
 */
export function makeStandardContactPage(
    Page,
    page,
    userLanguage,
    parentElement
) {
    // check if the contact page email admin button is disabled
    let displayEmailAdminButton = true;
    if (
        appDomain.options.contactPageEmailAdminButtonDisabled &&
        appDomain.options.contactPageEmailAdminButtonDisabled === true
    ) {
        displayEmailAdminButton = false;
    }
    // build the contact form
    const showFaqsForm = createElement(
        "button",
        { class: "btn btn-primary mt-3", id: "adminEmail" },
        [],
        i18n.getTranslation("contact_admin")
    );
    // build the admin address
    const adminData = page.PageContent[0];
    const adminEmailElement = createElement(
        "span",
        {
            class: "d-block",
            id: "adminEmail",
        },
        [],
        `<strong>${i18n.getTranslation("admin_email")}</strong> <a href="mailto:${siteInfo.email_info}">${siteInfo.email_info}</a>`
    );
    const Admin = createElement("address", { class: "mb-5" }, [
        createElement(
            "span",
            { class: "fw-bold d-block", id: "adminTitle" },
            [],
            adminData.AdminTitle
        ),
        createElement(
            "span",
            { class: "d-block", id: "adminName" },
            [],
            adminData.AdminName
        ),
        createElement(
            "span",
            { class: "d-inline-block", id: "adminAddress1" },
            [],
            adminData.AdminAddress1
        ),
        adminData.AdminAddress2
            ? createElement(
                  "span",
                  { id: "adminAddress2" },
                  [],
                  ", " + adminData.AdminAddress2
              )
            : ``,
        createElement("br"),
        createElement(
            "span",
            { class: "d-inline-block", id: "adminCity" },
            [],
            adminData.AdminCity
        ),
        createElement(
            "span",
            { class: "d-inline-block pe-1", id: "adminState" },
            [],
            ", " + adminData.AdminState
        ),
        createElement(
            "span",
            { id: "adminZipCode" },
            [],
            adminData.AdminZipCode
        ),
        siteInfo.phone
            ? createElement(
                  "span",
                  { class: "mt-3 d-block", id: "adminPhone" },
                  [],
                  `<strong>${i18n.getTranslation("admin_phone")}</strong> <a href="tel:+${siteInfo.phone}">${siteInfo.phone}</a>`
              )
            : createElement("br"),
        adminEmailElement,
        showFaqsForm,
    ]);

    // show faqs form
    if (displayEmailAdminButton) {
        showFaqsForm.addEventListener("click", () => {
            showContactFAQForm(userLanguage);
        });
    }
    // const address
    const Address = createElement("div", { class: "col-md-8" }, [Admin]);
    parentElement.appendChild(Address);
}
